import { createContext } from "react";
import { AppState, IdPUser, LoginWithIdPOptions } from "../idp-auth";
import { AccountRegion } from "../types/regionalApi";
import { UnifiedProvisionError, UnifiedProvisionPlanError } from "./constants";

export interface SSOInfo {
  idp_url: string;
  saml_request: string;
  relay_state: string;
}

interface Error {
  name: string;
  message: string;
  stack?: string;
}

/**
 * The unified auth state which, when combined with the auth methods, make up the return object of the `useUnifiedAuth` hook.
 */
export interface UnifiedAuthState {
  error?: Error | JSX.Element;
  /**
   * isAuthenticatedWithSendGrid returns true if the user is authenticated with SendGrid and ready to navigate to the dashboard.
   */
  isAuthenticatedWithSendGrid: boolean;
  /**
   * isLoadingWithSendGrid returns true if the user is attempting to retrieve data from SendGrid.
   */
  isLoadingWithSendGrid: boolean;
  ssoLoginInfo?: SSOInfo;
  isSSOLogin: boolean;
}

export const initialUnifiedAuthState: UnifiedAuthState = {
  isSSOLogin: false,
  isAuthenticatedWithSendGrid: false,
  isLoadingWithSendGrid: false,
};

export interface NavigateOptions extends Omit<LoginWithIdPOptions, "email"> {
  /**
   * The identifier needing to be looked up.
   */
  identifier: string;
  /**
   * An optional bot detection token.
   */
  bot_detection_token?: string;
}

export interface BasicAuthLoginOptions {
  /**
   * The username to log into.
   */
  username: string;

  /**
   * The password to use to log into it with.
   */
  password: string;

  /**
   * An optional bot detection token.
   */
  bot_detection_token?: string;
}

export type { LoginWithIdPOptions } from "../idp-auth";

export interface RedirectLoginOptions extends Omit<LoginWithIdPOptions, "email"> {
  /**
   * The type of login being performed
   */
  loginType: string;

  /**
   * The username to log into.
   * @see redirectLoginToIdPOptions to convert between RedirectLoginOptions and LoginWithIdPOptions
   */
  username: string;
}

export interface LogoutOptions {
  /**
   * Where to returnTo after you are finished with your logout.
   */
  returnTo?: string;
  /**
   * The region of the account, used to route the user to the correct regional api host.
   */
  region?: AccountRegion;

  /**
   * this setting logs out of all sendgrid stuff but not the IdP
   * used for the unified account switching flow
   */
  sendgridOnly?: boolean;

  /**
   * this setting logs out of the IdP but not sendgrid
   */
  twilioOnly?: boolean;

  /**
   * No redirect, means we don't want to redirect the user after the user logout.
   */
  noRedirect?: boolean;
}

export type UnifiedAccountLinkType = "login" | "signup";

export type ConfirmAccountLinkingResult =
  | {
      success: true;
    }
  | {
      success: false;
      error: UnifiedProvisionError;
    };

export type UnifiedProvisionEligibilityResult =
  | {
      eligible: true;
      email: string;
      has_mfa_exemption?: boolean;
    }
  | {
      eligible: false;
      error?: UnifiedProvisionPlanError;
      has_mfa_exemption?: boolean;
    };

export type AccountProfileResult = {
  friendly_name: string;
};

export type UserTypeResult = {
  type: string;
  region: AccountRegion;
  has_mfa_exemption?: boolean;
};

/**
 * Contains the authenticated state and authenticated methods provided by the useUnifiedAuth hook.
 */
export interface UnifiedAuthContextInterface extends UnifiedAuthState {
  /**
   * After the browser redirects back to the callback page, handles the IdP responses.
   */
  handleRedirectCallback: (appState?: AppState, user?: IdPUser) => Promise<void>;

  /**
   * ```js
   * isLoading;
   * ```
   * Returns true if either we are trying to pull data from SendGrid *OR* we are retrieving data from the IdP.
   */
  isLoading: boolean;

  /**
   * ```js
   * await loginWithIdP(options);
   * ```
   * Sends the browser to the IdP to log in. Clears the SendGrid session first unless the login
   * comes from account linking or switching.
   */
  loginWithIdP: (opts?: LoginWithIdPOptions) => Promise<void>;

  /**
   * ```js
   * await loginWithBasicAuth(options);
   * ```
   * loginWithBasicAuth takes an identifier and a password and attempts to log in using basic auth.
   */
  loginWithBasicAuth: (options?: BasicAuthLoginOptions) => Promise<void>;

  /**
   * ```js
   * await logout(options);
   * ```
   * clears the application session and redirect after all the sessions have been cleared.
   */
  logout: (options: LogoutOptions) => Promise<void>;

  /**
   * ```js
   * await navigate('');
   * ```
   * navigate takes an option and attempts to redirect to the correct system to authenticate.
   */
  navigate: (opts: NavigateOptions) => Promise<void>;

  /**
   * ```js
   * redirectToUnifiedAuthWithLinking();
   * ```
   * Redirects to the unified auth page for account linking.
   */
  redirectToUnifiedAuthWithLinking: (
    linkType: UnifiedAccountLinkType,
    prefillEmail?: string
  ) => Promise<void>;

  /**
   * ```js
   * skipUnifiedAccountLinking();
   * ```
   * Skips the unified account linking process and redirects to mako.
   */
  skipUnifiedAccountLinking: () => void;

  /**
   * ```js
   * goToLoginIfNotAuthenticatedWithSendgrid();
   * ```
   * Protects the unified account linking routes from unauthorized access.
   */
  goToLoginIfNotAuthenticatedWithSendgrid: () => void;

  /**
   * ```js
   * confirmUnifiedAccountLinking();
   * ```
   * Confirms the unified account linking process.
   */
  confirmUnifiedAccountLinking: (
    accountRegion: AccountRegion
  ) => Promise<ConfirmAccountLinkingResult>;

  fetchTokenAndRedirect: (
    access_token: string,
    user?: IdPUser,
    opts?: {
      // used for multi account access to select a specific Sendgrid UserId
      userId?: string;
      // if true, call fetchStatus to set needsOnboarding
      checkOnboarding?: boolean;
      resellerFallThrough?: { errorPage: string };
    }
  ) => Promise<"session" | "ipam_blocked" | "banned" | "maat" | "no_session">;
  fetchUnifiedAccountLinkingEligibility: (
    accountRegion: AccountRegion
  ) => Promise<UnifiedProvisionEligibilityResult>;

  getAccessTokenSilentlyOrLogin: ({
    fromUnifiedAccountSwitching,
  }: {
    fromUnifiedAccountSwitching: boolean;
  }) => Promise<string | undefined>;

  signupUser: (
    access_token: string,
    id_token: string,
    appState?: AppState,
    user?: IdPUser
  ) => Promise<void>;
}

/**
 * @ignore
 */
const stub = (): never => {
  throw new Error("You forgot to wrap your component in <UnifiedAuthProvider>.");
};

/**
 * @ignore
 */
export const initialContext = {
  ...initialUnifiedAuthState,
  isLoading: false,
  navigate: stub,
  loginWithBasicAuth: stub,
  loginWithIdP: stub,
  handleRedirectCallback: stub,
  logout: stub,
  redirectToUnifiedAuthWithLinking: stub,
  skipUnifiedAccountLinking: stub,
  getAccessTokenSilentlyOrLogin: stub,
  goToLoginIfNotAuthenticatedWithSendgrid: stub,
  confirmUnifiedAccountLinking: stub,
  fetchTokenAndRedirect: stub,
  fetchUnifiedAccountLinkingEligibility: stub,
  signupUser: stub,
};

export const UnifiedAuthContext = createContext<UnifiedAuthContextInterface>(initialContext);
